import { useMemo } from "react";
import keyBy from "lodash.keyby";

import { type AssignedTrainerWithId } from "@fitness-app/data-models/entities/TeamMember";
import { type TraineeWithAssignedTrainer } from "@fitness-app/data-models/entities/Trainee";

import { useAppSelector } from "~/store/initializeStore";

/**
 * Trainers owning any of the listed clients, in the shape the tasks table expects (TRE-1841).
 *
 * The map is narrowed to trainers actually present in the list, so the column filter only offers
 * values that return rows. Returns `undefined` when disabled — the table hides the whole column.
 */
export const useClientsTrainersById = (
  clients: TraineeWithAssignedTrainer[],
  enabled: boolean,
): Record<string, AssignedTrainerWithId> | undefined => {
  const teamMembers = useAppSelector((state) => state.team.trainers);

  return useMemo(() => {
    // While the team list is still loading every row would render an empty trainer cell, which
    // reads as "no trainer" — keep the column out until the members are there.
    if (!enabled || !teamMembers.length) {
      return undefined;
    }

    const assignedTrainerIds = new Set(clients.map((client) => client.assignedTrainerId).filter(Boolean));

    const assignedTrainers = teamMembers
      .filter((member) => assignedTrainerIds.has(member.id))
      .map(
        (member): AssignedTrainerWithId => ({
          assignedTrainerId: member.id,
          trainer: {
            id: member.id,
            firstName: member.firstName,
            lastName: member.lastName,
            email: member.email,
            status: member.status,
            user: member.userId ? { id: member.userId, avatarUrl: member.user?.avatarUrl ?? null } : null,
          },
        }),
      );

    return keyBy(assignedTrainers, "assignedTrainerId");
  }, [clients, enabled, teamMembers]);
};
